import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useLazyGetExaminationDetailQuery,
  useLazyGetExaminationByAppointmentQuery,
  useStartExaminationMutation,
  useCompleteExaminationMutation,
  useCancelExaminationMutation,
} from "../../../store";
import {
  useListDiagnosesMutation,
  useListComplaintsMutation,
  useListMedicineTemplatesMutation,
  useListFoodsMutation,
  useListLabTestsMutation,
  useListAllergiesMutation,
} from "../../../store/systemVariablesApi";
import useAuth from "../../../hooks/useAuth";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { createAutoSaveRegistry } from "./autoSaveRegistry";
import useTerms from "../../../hooks/useTerms";

export default function useExaminationSession() {
  const terms = useTerms();
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [startExamination] = useStartExaminationMutation();
  const [getByAppointment] = useLazyGetExaminationByAppointmentQuery();
  const canCreateExam = useHasPermission("examinations:create");
  const [getDetail, { error: detailError }] = useLazyGetExaminationDetailQuery();
  const [completeExamination] = useCompleteExaminationMutation();
  const [cancelExamination] = useCancelExaminationMutation();

  // Support lists consumed by Tedaviler / Tetkik / Randevular drawers in
  // session mode. The backend detail payload only ships patient-scoped
  // records; the autocomplete option sources below are fetched once after
  // the session boots so the drawers are usable for in-session create.
  const [listDiagnoses] = useListDiagnosesMutation();
  const [listComplaints] = useListComplaintsMutation();
  const [listMedicineTemplates] = useListMedicineTemplatesMutation();
  const [listFoods] = useListFoodsMutation();
  const [listLabTests] = useListLabTestsMutation();
  const [listAllergies] = useListAllergiesMutation();

  const [examinationId, setExaminationId] = useState(null);
  const [session, setSession] = useState(null);
  const [supportData, setSupportData] = useState({
    diagnoses: [],
    complaints: [],
    medicineTemplates: [],
    foods: [],
    labTests: [],
    allergies: [],
  });
  const [isBooting, setIsBooting] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [completeError, setCompleteError] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");

  const bootedForRef = useRef(null);
  const autoSaveRegistry = useMemo(() => createAutoSaveRegistry(), []);

  useEffect(() => {
    if (!token || !appointmentId) return;
    if (bootedForRef.current === appointmentId) return;
    bootedForRef.current = appointmentId;
    (async () => {
      try {
        setIsBooting(true);
        setBootError(null);
        setSession(null);
        setExaminationId(null);
        // Önce mevcut muayeneyi oku (özet / başkasının muayenesi başlatma
        // yetkisi istemez). Başlatma yalnız: muayene yok + randevu planlı +
        // kişi randevunun doktoru (ya da Admin) ve muayene yapma yetkisi var.
        const found = await getByAppointment({ token, appointmentId }).unwrap();
        let exam = found?.data?.examination || null;
        if (!exam) {
          const apptStatus = found?.data?.appointmentStatus;
          const isOwn = String(found?.data?.appointmentDoctorId || "") === String(auth?._id || "");
          const mayStart = canCreateExam && (isOwn || auth?.role === "Admin");
          if (apptStatus !== "scheduled" || !mayStart) {
            throw new Error(
              apptStatus === "scheduled"
                ? `Bu randevunun ${terms.examLower}si henüz başlatılmadı.`
                : `Bu randevuya ait ${terms.examLower} kaydı bulunmuyor.`
            );
          }
          const startRes = await startExamination({ token, appointmentId }).unwrap();
          exam = startRes?.data?.examination;
        }
        if (!exam?._id) throw new Error(`${terms.exam} başlatılamadı`);
        setExaminationId(exam._id);
        const detailRes = await getDetail({ token, id: exam._id }).unwrap();
        setSession(detailRes?.data || null);

        // Load support data in parallel. Any failure here is non-fatal —
        // the relevant drawer autocomplete simply shows an empty list.
        try {
          const [
            diagnosesRes,
            complaintsRes,
            medicinesRes,
            foodsRes,
            labTestsRes,
            allergiesRes,
          ] = await Promise.all([
            listDiagnoses({ token, status: "active", limit: 200 }).unwrap(),
            listComplaints({ token, status: "active", limit: 500 }).unwrap(),
            listMedicineTemplates({
              token,
              status: "active",
              limit: 200,
            }).unwrap(),
            listFoods({ token, status: "active", limit: 500 }).unwrap(),
            listLabTests({ token, status: "active", limit: 200 }).unwrap(),
            listAllergies({ token, status: "active", limit: 500 })
              .unwrap()
              .catch(() => ({ data: { items: [] } })),
          ]);
          setSupportData({
            diagnoses: diagnosesRes?.data?.diagnoses || [],
            complaints:
              complaintsRes?.data?.complaints ||
              complaintsRes?.data?.items ||
              [],
            medicineTemplates:
              medicinesRes?.data?.items ||
              medicinesRes?.data?.medicines ||
              [],
            foods: foodsRes?.data?.foods || [],
            labTests: labTestsRes?.data?.items || [],
            allergies: allergiesRes?.data?.items || [],
          });
        } catch (supportErr) {
          console.warn("Support data fetch partial failure", supportErr);
        }
      } catch (err) {
        console.error("Session bootstrap failed", err);
        setBootError(err);
      } finally {
        setIsBooting(false);
      }
    })();
  }, [
    token,
    appointmentId,
    startExamination,
    getByAppointment,
    getDetail,
    auth,
    canCreateExam,
    listDiagnoses,
    listComplaints,
    listMedicineTemplates,
    listFoods,
    listLabTests,
    listAllergies,
    terms,
  ]);

  const refetchDetail = useCallback(async () => {
    if (!token || !examinationId) return;
    try {
      const res = await getDetail({ token, id: examinationId }).unwrap();
      setSession(res?.data || null);
    } catch (err) {
      console.error("Detail refetch failed", err);
    }
  }, [token, examinationId, getDetail]);

  // Bitmiş muayene, ya da başkasının (doktoru olmadığın) muayenesi salt-okunur.
  const isReadOnly = useMemo(() => {
    const exam = session?.examination;
    if (!exam) return false;
    if (exam.status === "completed" || exam.status === "cancelled") return true;
    if (auth?.role === "Admin") return false;
    const doctorId = exam.doctor?._id || exam.doctor;
    return !canCreateExam || String(doctorId || "") !== String(auth?._id || "");
  }, [session, auth, canCreateExam]);

  const handleComplete = useCallback(async () => {
    setCompleteError(null);
    setIsCompleting(true);
    try {
      // Önce tüm kayıtlı flush callback'lerini tetikle (debounced
      // savePhysical gibi dirty bekleyenler) — sonra pending in-flight
      // save'lerin tamamlanmasını bekle.
      try {
        await autoSaveRegistry.flushAll();
      } catch (_) {
        // İndividual flush hatası registry üzerinden hasFailures'a yansır.
      }
      await autoSaveRegistry.waitForIdle(5000);
      if (autoSaveRegistry.hasFailures) {
        setCompleteError("Kaydedilemeyen değişiklikler var — önce onları çöz");
        return;
      }
      const res = await completeExamination({ token, id: examinationId }).unwrap();
      const updated = res?.data?.examination;
      setSession((prev) =>
        prev ? { ...prev, examination: updated || prev.examination } : prev
      );
    } catch (err) {
      setCompleteError(err?.data?.message || `${terms.exam} tamamlanırken hata oluştu`);
    } finally {
      setIsCompleting(false);
    }
  }, [token, examinationId, completeExamination, autoSaveRegistry, terms]);

  const handleCancel = useCallback(
    async (reason) => {
      setIsCancelling(true);
      try {
        await cancelExamination({ token, id: examinationId, reason }).unwrap();
        navigate("/Randevular");
      } catch (err) {
        console.error("Cancel failed", err);
      } finally {
        setIsCancelling(false);
      }
    },
    [token, examinationId, cancelExamination, navigate]
  );

  return {
    token,
    isLoading: isBooting,
    error: bootError || detailError,
    session,
    supportData,
    examinationId,
    isReadOnly,
    autoSaveRegistry,
    saveStatus,
    setSaveStatus,
    refetchDetail,
    handleComplete,
    isCompleting,
    completeError,
    clearCompleteError: () => setCompleteError(null),
    handleCancel,
    isCancelling,
  };
}
